<template>
  <BLayoutExpansion class="FormTemplatesFormSheet">
    <template #header>
      <v-row dense justify="space-between" align="center" style="margin: -12px -4px;">
        <v-col>
          <b v-text="form.name" />
          <span class="grey--text" v-text="` (${form.code})`" />
        </v-col>

        <v-col cols="auto" style="margin: -12px 0;" @click.stop>
          <BTooltipButton
            v-for="item in buttonList"
            :key="item.action"
            :tooltip="item.tooltip"
            :icon="item.icon"
            depressiconed
            top
            small
            @click="handleClick(item)"
          />
        </v-col>
      </v-row>
    </template>

    <v-row dense class="pb-2 mb-2">
      <v-col cols="12" sm="6">
        <BTextField
          v-model="form.name"
          validate="required|max:31"
          label="Название"
          :disabled="disabled"
          :skeleton="skeleton"
          :max-counter="31"
          counter
          :name="sheetName"
        />
      </v-col>

      <v-col cols="12" sm="2">
        <BTextField
          v-model="form.code"
          validate="required"
          label="Код"
          :disabled="disabled"
          :skeleton="skeleton"
          name="sheet-code"
        />
      </v-col>

      <v-spacer />

      <v-col cols="12">
        <BTextarea
          v-if="withHint"
          v-model="form.hint"
          label="Описание"
          :skeleton="skeleton"
          :rows="3"
          name="hint"
          class="mt-2"
        />
      </v-col>

      <v-col cols="auto">
        <v-layout row align-center>
          <v-checkbox
            v-model="form.vertical"
            label="Отобразить вертикально?"
            dense
            hide-details
            class="pa-0 my-0 ml-2"
          />
          <v-checkbox
            v-model="form.required"
            label="Лист обязателен к заполнению?"
            dense
            hide-details
            class="pa-0 my-0 ml-2"
          />
        </v-layout>
      </v-col>
    </v-row>

    <template v-for="(item, index) in form.cells">
      <FormTemplatesFormCell
        :key="item.uuid"
        :form="item"
        :disabled="disabled"
        :skeleton="skeleton"
        :vertical="form.vertical"
        class="mb-2 js-cell"

        :upable="index > 0"
        :downable="form.cells.length > 1 && form.cells.length - 1 !== index"

        @sortable-positions-up="sortCell(item, index, index - 1)"
        @sortable-positions-down="sortCell(item, index, index + 1)"
        @copy="copyCell(item, index)"
        @delete="deleteCell(item, index)"
      />
    </template>

    <v-row dense>
      <v-col v-if="$errors.has(fieldName)">
        <v-input
          :error="$errors.has(fieldName)"
          :error-messages="$errors.first(fieldName)"
        />
      </v-col>
      <v-spacer v-else />
      <v-col cols="auto">
        <v-skeleton-loader :loading="skeleton" type="button">
          <v-btn
            depressed
            class="js-btn"
            @click="$emit('copy')"
            v-text="'Скопировать лист'"
          />
        </v-skeleton-loader>
      </v-col>
      <v-col cols="auto">
        <v-skeleton-loader :loading="skeleton" type="button">
          <v-btn
            depressed
            class="js-btn"
            @click="addCell($event)"
            v-text="'Добавить ячейку'"
          />
        </v-skeleton-loader>
      </v-col>
    </v-row>
  </BLayoutExpansion>
</template>

<script>
import {
  SORTABLE_POSITIONS_DOWN,
  SORTABLE_POSITIONS_UP,
  DELETE,
} from '@/helpers/enums/table-actions'
import {
  HINT_SHOW,
  HINT_HIDE,
} from '@/views/FormTemplates/table-actions'
import { createModalRemove } from '@/helpers/createModalRemove'
import { getUuid } from '../utils'
import uniqueId from 'lodash/uniqueId'

import BTextarea from '@/components/forms/BTextarea.vue'
import BLayoutExpansion from '@/components/layouts/BLayoutExpansion.vue'
import BTextField from '@/components/forms/BTextField.vue'
import BTooltipButton from '@/components/common/BTooltipButton.vue'
import FormTemplatesFormCell, {
  createDefaultData as createDefaultDataCell,
} from './FormTemplatesFormCell.vue'


export const createDefaultData = (code, uuid, uuid2) => ({
  uuid,
  code: `sheet$${code}`,
  name: `Лист ${code}`,
  vertical: false,
  required: true,
  cells: [createDefaultDataCell(1, uuid2)],
})

export const createPrefillData = (uuid, code, name, vertical = false, required = true, cells) => ({
  uuid,
  code: `sheet$${code}`,
  name: name ? `${name}` : `Лист ${code}`,
  vertical,
  required,
  cells,
})

export default {
  name: 'FormTemplatesFormSheet',
  inject: ['$validator'],
  components: {
    BTextarea,
    BLayoutExpansion,
    BTextField,
    BTooltipButton,
    FormTemplatesFormCell,
  },
  props: {
    fieldName: String,
    skeleton: Boolean,
    disabled: Boolean,

    deleteable: Boolean,
    upable: Boolean,
    downable: Boolean,
    form: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isEditName: false,
    hint: HINT_SHOW,
  }),
  computed: {
    buttonList() {
      return [
        { ...this.hint, action: 'toggle:hint', onClick: this.toggleHint },
        this.upable && SORTABLE_POSITIONS_UP,
        this.downable && SORTABLE_POSITIONS_DOWN,
        this.deleteable && DELETE,
      ].filter(Boolean)
    },
    withHint() {
      return this.hint === HINT_HIDE
    },
    sheetName() {
      return uniqueId('sheet-name-')
    },
  },
  created() {
    if (this.form?.hint) this.hint = HINT_HIDE
  },
  methods: {
    async addCell(event) {
      const list = this.form.cells
      const uuid = await getUuid()
      const codeNumbers = list.map((_) => _.code.replace(/\D+/g, ''))
      const code = codeNumbers?.length ? Math.max(...codeNumbers) : 0
      const data = createDefaultDataCell(+code + 1, uuid)
      list.push(data)

      this.$validator.errors.remove(this.fieldName)

      const module = await import(
        /* webpackChunkName: "vue-scrollto" */
        'vue-scrollto'
      )

      const vueScrollTo = module.default

      vueScrollTo.scrollTo(
        event.target.parentNode.parentNode.parentNode.previousElementSibling,
        200,
        { offset: -70 },
      )
    },
    sortCell(data, oldIndex, newIndex) {
      const list = this.form.cells
      list.splice(oldIndex, 1, list[newIndex])
      list.splice(newIndex, 1, data)
    },
    async deleteCell(data, index) {
      const name = data.name && `"${data.name}"`
      const propsData = {
        titleText: `Удалить ячейку ${name} <small>(${data.code})</small>`,
      }
      const modal = await createModalRemove(this, propsData)
      const result = await modal.open()
      if (!result) return
      const list = this.form.cells
      list.splice(index, 1)

      if (list.length === 0) {
        this.$validator.errors.add({
          field: this.fieldName,
          msg: 'Необходимо добавить ячейки.',
        })
      }
    },
    async copyCell(data, index) {
      const list = this.form.cells
      const uuid = await getUuid()
      const codeNumbers = list.map((_) => _.code.replace(/\D+/g, ''))
      const code = Math.max(...codeNumbers) || 1

      let form = createDefaultDataCell(+code + 1, uuid)

      form = {
        ...form,
        ...JSON.parse(JSON.stringify(data)),
        uuid,
        code: form.code,
        name: `${data.name} Копия`,
      }

      list.splice(index + 1, 0, form)
    },
    toggleHint() {
      this.hint = this.hint === HINT_SHOW ? HINT_HIDE : HINT_SHOW
    },
    handleClick(item) {
      if (item.onClick) item.onClick()
      else this.$emit(item.action)
    },
  },
}
</script>
